import { List, Map } from 'immutable';
import getIn from 'lodash/get';
import isFunction from 'lodash/isFunction';

import * as Constants from '../constants';
import { getInDomain } from '../domains/externalUtils';
import { getAnnotationsById, getDetails, getTrackById } from '../lib/EntityUtils';
import { getPandoraIdFromMusicId } from '../lib/PandoraIdUtil';
import {
  getLyricSnippet,
  getLyricSnippetAPS,
  isAudioWarning,
  isTrackThumbedDown,
  isTrackThumbedUp,
  shouldTrackPersistInSessionHistory,
} from '../lib/PlaylistTrackUtils';
import { getTrackAlbumPath, getTrackArtistPath, getTrackSongPath } from '../lib/RouteUtils';
import { isStationVoiceTrack } from '../lib/VoiceTrackUtils';

const modelResolverKey = 'MODEL_RESOLVER_WEB_APP';
// TODO: this should probably become something we construct at runtime
const MODEL_MAP = {
  APSItem: {
    [modelResolverKey]: (state, pandoraId) => {
      const item = getInDomain('playback.selectors.getSessionItem', Map())(state, pandoraId);
      const annotation = getAnnotationsById(state, pandoraId);
      let track = getInDomain('playback.selectors.getCurrentItemAudioTrack', Map())(state);

      track = track?.set('sessionItem', item);

      return annotation ? annotation.merge(track) : track;
    },
    type: track => {
      return track.getIn(['sessionItem', 'type']);
    },
    sessionTrackToken: track => {
      return track.getIn(['sessionItem', 'pandoraId']);
    },
    trackToken: 'pandoraId',
    name: 'name',
    shareableUrlPath: 'shareableUrlPath',
    lyricSnippet: (track, state) => {
      const lyricSnippet = getLyricSnippetAPS(state);
      return lyricSnippet && List(lyricSnippet.split('\n'));
    },
    artist: (track, state) => {
      const artistId = track.get('artistId');
      const annotation = getAnnotationsById(state, artistId);
      return annotation && annotation.merge(getDetails(state, artistId));
    },
    artistName: (track, state) => {
      return getInDomain('catalog.utils.getTrackAuthor')(state, track);
    },
    artistPandoraId: 'artistId',
    artistPath: (track, state) => {
      const annotation = getAnnotationsById(state, track.get('artistId'));
      return annotation && annotation.get('shareableUrlPath');
    },
    albumTitle: (track, state) => {
      const annotation = getAnnotationsById(state, track.get('albumId'));
      return annotation && annotation.get('name');
    },
    albumPath: (track, state) => {
      const annotation = getAnnotationsById(state, track.get('albumId'));
      return annotation && annotation.get('shareableUrlPath');
    },
    shouldPersistInSessionHistory: track => {
      return shouldTrackPersistInSessionHistory(track);
    },
    shouldShowReplay: () => {
      return true;
    },
    shouldShowThumbs: () => {
      return true; // this will need to take track type into account before turning APS on for stations
    },
    isReplayEnabled: track => {
      return track
        .getIn(['sessionItem', 'interactions'], List())
        .includes(Constants.Playback.PLAYBACK_CONTROLS.REPLAY);
    },
    isFeedbackEnabled: track => {
      return track
        .getIn(['sessionItem', 'interactions'], List())
        .includes(Constants.Playback.PLAYBACK_CONTROLS.THUMBS);
    },
    isVoiceTrack: track => {
      return getInDomain('catalog.utils.isCatalogVoiceTrack')(track);
    },
    trackThumbedDown: track => {
      return track.getIn(['sessionItem', 'rating'], 0) === -1;
    },
    trackThumbedUp: track => {
      return track.getIn(['sessionItem', 'rating'], 0) === 1;
    },
  },
  PlaylistTrack: {
    [modelResolverKey]: getTrackById,
    type: 'trackType',
    trackToken: 'trackToken',
    name: (track, state) => {
      if (isStationVoiceTrack(track)) {
        const trackAnnotation = getAnnotationsById(state, track.get('pandoraId'));
        return trackAnnotation && trackAnnotation.get('name');
      }
      return track.get('songTitle');
    },
    shareableUrlPath: track => {
      return !isAudioWarning(track) && getTrackSongPath(track);
    },
    lyricSnippet: (track, state) => {
      const lyricData = getLyricSnippet(track, state);
      return lyricData && lyricData.get('lines');
    },
    artist: (track, state) => {
      // eslint-disable-next-line no-use-before-define
      const artistId = getFromModel(track, 'PlaylistTrack', 'artistPandoraId', state);
      const annotation = getAnnotationsById(state, artistId) || Map();
      const detail =
        !isStationVoiceTrack(track) ||
        annotation.get('type') === Constants.TrackType.VoiceTrackAuthorType.ARTIST
          ? getDetails(state, artistId)
          : Map();
      return annotation && annotation.merge(detail);
    },
    artistName: (track, state) => {
      if (isStationVoiceTrack(track)) {
        const attributes = getInDomain('playback.utils.getVoiceTrackAttributes')(state, track);
        return (attributes && attributes.artistName) || '';
      }
      return track.get('artistName');
    },
    artistPandoraId: (track, state) => {
      let artistId;
      if (isStationVoiceTrack(track)) {
        const trackAnnotation = getAnnotationsById(state, track.get('pandoraId'));
        artistId = trackAnnotation && trackAnnotation.get('authorId');
      } else if (track.get('artistMusicId')) {
        artistId = getPandoraIdFromMusicId(track.get('artistMusicId'));
      }
      return artistId;
    },
    artistPath: track => {
      return !isAudioWarning(track) && getTrackArtistPath(track);
    },
    albumTitle: track => {
      return !isAudioWarning(track) && track.get('albumTitle');
    },
    albumPath: track => {
      return !isAudioWarning(track) && getTrackAlbumPath(track);
    },
    shouldPersistInSessionHistory: track => {
      return shouldTrackPersistInSessionHistory(track);
    },
    shouldShowReplay: (track, state) => {
      return getInDomain('playback.utils.shouldShowReplayControl')(state, track);
    },
    isReplayEnabled: (track, state) => {
      return getInDomain('playback.utils.getEnabledPlaybackControls')(state, track).contains(
        Constants.Playback.PLAYBACK_CONTROLS.REPLAY,
      );
    },
    shouldShowThumbs: track => {
      return !isStationVoiceTrack(track);
    },
    isFeedbackEnabled: (track, state) => {
      return getInDomain('playback.utils.allowFeedbackForTrack')(state, track);
    },
    isVoiceTrack: track => {
      return isStationVoiceTrack(track);
    },
    trackThumbedDown: track => {
      return isTrackThumbedDown(track);
    },
    trackThumbedUp: track => {
      return isTrackThumbedUp(track);
    },
  },
};

export function resolveModelFromKey(state, key, type) {
  const resolver = getIn(MODEL_MAP, `${type}.${modelResolverKey}`);
  if (!isFunction(resolver)) {
    // Error
    return null;
  }
  return resolver(state, key);
}

export function getFromModel(model, type, attr, state) {
  if (!Map.isMap(model)) {
    // Error, model must be a map
    return null;
  }
  const value = getIn(MODEL_MAP, `${type}.${attr}`);
  if (value) {
    if (isFunction(value)) return value(model, state);
    return model.get(value, null);
  }
  return model.get(attr, null);
}

export function getFirstModel(types = [], state, lookupKey) {
  if (!lookupKey) return null;
  let result;
  for (let i = 0; i < types.length; i++) {
    result = resolveModelFromKey(state, lookupKey, types[i]);
    if (result) return result;
  }
  return null;
}
